import { useDispatch, useSelector } from 'react-redux';

import { useEffect, useState } from 'react';
import { ApplicationState, ProductServiceOrder } from '@mews-commander/types';
import { ConvertedCurrencyValue, LocalizedDateTimeFromUtc, Parenthesized } from '@mews-commander/ui';
import { Stack, StackItem } from '@optimus-web/core';
import { MatrixMapping } from '@mews/b2b-ui';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedText } from '@mews/framework';
import { Identifiable } from '@mews/std';
import { TextKey } from '@mews-commander/enums';
import { getDescendingProductServiceOrdersByOwnerId } from '@mews-commander/legacy-entities/src/ducks/productServiceOrders';
import { fetchProductServiceOrders } from '@mews-commander/legacy-entities/src/actions/productServiceOrders';
import { ResponsiveTable } from '@mews-commander/ui/legacy';
import { ProductServiceOrderLink, AccountLink, ProductServiceOrderNoteText } from '../../../../../components';
import ServiceOrderActionCell from './ServiceOrderActionCell';
import { ServiceOrderStateTag } from './ServiceOrderStateTag';

export const mapping = [
    {
        valueKey: 'id',
        title: <LocalizedText textKey={TextKey.Order} />,
        render: ({ id }) => <ProductServiceOrderLink id={id} />,
    },
    {
        valueKey: 'createdUtc',
        title: <LocalizedText textKey={TextKey.Created} />,
        render: ({ createdUtc, creatorProfileId }) =>
            !!createdUtc && (
                <Stack wrap>
                    <StackItem>
                        <LocalizedDateTimeFromUtc utcString={createdUtc} />
                        {UnicodeEntity.NoBreakSpace}
                    </StackItem>
                    <StackItem>
                        <Parenthesized>
                            <AccountLink id={creatorProfileId} />
                        </Parenthesized>
                    </StackItem>
                </Stack>
            ),
    },
    {
        valueKey: 'startUtc',
        title: <LocalizedText textKey={TextKey.Start} />,
        render: ({ startUtc }) => !!startUtc && <LocalizedDateTimeFromUtc utcString={startUtc} />,
    },
    {
        valueKey: 'id',
        title: <LocalizedText textKey={TextKey.Notes} />,
        render: ({ id }) => <ProductServiceOrderNoteText serviceOrderId={id} />,
        columnWidth: '25%',
    },
    {
        valueKey: 'state',
        title: <LocalizedText textKey={TextKey.Status} />,
        render: ({ state }) => <ServiceOrderStateTag serviceOrderState={state} />,
    },
    {
        valueKey: 'totalCost',
        title: <LocalizedText textKey={TextKey.TotalAmount} />,
        render: ({ totalCost }) => <ConvertedCurrencyValue amount={totalCost} />,
    },
    {
        valueKey: 'state',
        title: '',
        render: ({ id, state }) => <ServiceOrderActionCell state={state} id={id} />,
    },
] satisfies Array<MatrixMapping<ProductServiceOrder>>;

export const ServiceOrderTable = ({ id }: Identifiable) => {
    const dispatch = useDispatch();
    const [initialized, setInitialized] = useState(false);
    useEffect(() => {
        if (!initialized) {
            setInitialized(true);
            dispatch(fetchProductServiceOrders({ ownerIds: [id], extent: { notes: true } }));
        }
    }, [dispatch, initialized, setInitialized, id]);
    const serviceOrders = useSelector((state: ApplicationState) =>
        getDescendingProductServiceOrdersByOwnerId(state, { ownerId: id })
    );
    return <ResponsiveTable mapping={mapping} source={serviceOrders} fixedHeader />;
};
